import { parseISO } from 'date-fns'
import {
    Addon,
    BundledChallenge,
    Receipt,
    Summary,
    JoinEventRes
} from 'src/services/types/joinEventV2'
import { Challenge } from 'src/services/types/searchEvents'
import { ILoginUser } from 'src/services/types/user'
import {
    EventType,
    Gender,
    Order,
    Product,
    TrackedEvents,
    Traits,
    ViewEventProps,
    GoogleBusinessVertical,
    RemarketingV2,
    GTMEvents
} from './types'
import TagManager from 'react-gtm-module'

export function trackUser(user?: ILoginUser) {
    const traits = { ...setTraits(user) }
    const userId = String(user?.userId) ?? null
    try {
        global.analytics.identify(userId, traits, null, null)
    } catch (e) {
        return console.error('Segment Analytics Error', e)
    }
}

export function trackRegistration(user: ILoginUser) {
    try {
        const traits = {
            currency: 'USD',
            price: 0, //using price cause Segment maps price to value for FB events.
            ...setTraits(user)
        }
        global.analytics.track(TrackedEvents.REGISTRATION, traits)
    } catch (e) {
        return console.error('Segment Analytics Error', e)
    }
}

export function trackInitiateCheckout(eventProps) {
    try {
        const { productData, user } = eventProps
        const { summary, addons } = productData
        const type = summary.eventTypeString
        const price = getPriceByCategory(type, addons, summary)

        global.analytics.track(
            TrackedEvents.CHECKOUT_STARTED,
            {
                price: price,
                product_id: summary.challengeId,
                currency: 'USD',
                name: summary.name,
                category: type,
                brand: 'YES.FIT',
                sku: String(summary.challengeId)
            },
            { context: { traits: { ...setTraits(user) } } }
        )
    } catch (e) {
        return console.error('Segment Analytics Error', e)
    }
}

export function trackAddToCart(items: BundledChallenge[], user?: ILoginUser) {
    const products = combinedChallengesAndAddOns(items)

    try {
        products.forEach((product: Product) => {
            global.analytics.track(TrackedEvents.PRODUCT_ADDED, product, {
                context: { traits: { ...setTraits(user) } }
            })
            gtmAddToCart(product, user)
        })
    } catch (e) {
        return console.error('Segment Analytics Error', e)
    }
}

export function trackRemoveFromCart(items: BundledChallenge[], user?: ILoginUser) {
    const products = combinedChallengesAndAddOns(items)
    try {
        products.forEach((product: Product) => {
            global.analytics.track(TrackedEvents.PRODUCT_REMOVED, product, {
                context: { traits: { ...setTraits(user) } }
            })
            gtmRemoveFromCart(product, user)
        })
    } catch (e) {
        return console.error('Segment Analytics Error', e)
    }
}

export function trackPurchase(receipt: Receipt, user?: ILoginUser) {
    const purchasedChallenges = receipt.bundle.challenges
    const taggedProducts = combinedChallengesAndAddOns(purchasedChallenges)
    const order: Order = mapOrder(taggedProducts, receipt)
    try {
        global.analytics.track(TrackedEvents.ORDER_COMPLETE, order, {
            context: { traits: { ...setTraits(user) } }
        })
        gtmTagsSale(taggedProducts, receipt.total, user)
    } catch (e) {
        return console.error('Segment Analytics Error', e)
    }
}

export function trackView(eventProps: ViewEventProps) {
    const { productData, user } = eventProps
    const { summary, addons } = productData
    const type = summary.eventTypeString
    const price = getPriceByCategory(type, addons, summary)
    try {
        global.analytics.track(
            TrackedEvents.PRODUCT_VIEWED,
            {
                price: price,
                product_id: summary.challengeId,
                currency: 'USD',
                name: summary.name,
                category: type,
                brand: 'YES.FIT',
                sku: String(summary.challengeId)
            },
            {
                context: { traits: { ...setTraits(user) } }
            }
        )

        gtmViewItem(productData, price, user)
    } catch (e) {
        return console.error('Segment Analytics Error', e)
    }
}
function combinedChallengesAndAddOns(challenges) {
    return challenges.flatMap((c) => {
        const price = c.items.reduce(
            (acc: number, addon: Addon) => acc + addon.itemPrice,
            0
        )
        const challenge: Product = mapChallenge(c, price)
        //const challengeAddOns = c.items.length >= 1 ? mapAddOns(c) : []
        return [challenge /*, ...challengeAddOns*/]
    }) as Product[]
}

function mapChallenge(challenge: Challenge | BundledChallenge, price: number): Product {
    const productCategory = challenge.eventTypeString

    return {
        product_id: challenge.challengeId,
        name: challenge.name,
        category: productCategory,
        price: price,
        quantity: 1,
        brand: 'YES.FIT',
        sku: String(challenge.challengeId)
    }
}

/*
function mapAddOns(purchasedChallenge: any): Product[] {
    const challengeAddOns: Product[] = []
    challengeAddOns.push(
        ...purchasedChallenge.items.map((addon) => ({
            product_id: addon.challengeAddonId * 10000000,
            name: addon.description,
            category: addon.challengeAddonType.name,
            price: addon.itemPrice,
            quantity: 1,
            brand: 'YES.FIT',
            sku: String(addon.challengeAddonId * 10000000)
        }))
    )
    return challengeAddOns
}
*/

function mapOrder(products: Product[], receipt: Receipt): Order {
    return {
        order_id: String(receipt.orderID),
        currency: 'USD',
        revenue: receipt.charged,
        affiliation: 'Yes.Fit',
        total: receipt.total,
        discount: receipt.discount,
        coupon: getPromoCode(receipt),
        products: products,
        subtotal: receipt.subTotal
    }
}

function mapProductsToRemarketing(products: Product[]): RemarketingV2[] {
    const productsForRemarketing: RemarketingV2[] = products.map((product: Product) => {
        return mapProductToRemarketing(product)
    })
    return productsForRemarketing
}

function mapProductToRemarketing(product: Product): RemarketingV2 {
    return {
        id: product.product_id,
        google_business_vertical: GoogleBusinessVertical.Retail
    }
}

function getPromoCode(receipt: Receipt): string {
    if (receipt.vouchers.length > 0) {
        const promoCodes = receipt.vouchers.filter((v) => v.isPromotion === false)
        if (promoCodes.length) {
            return String(promoCodes[0].code ?? '')
        }
    }
    return null
}

function getPriceByCategory(productCategory: any, addons: Addon[], summary: Summary) {
    return productCategory === EventType.Store
        ? addons.reduce((acc, addon) => acc + addon.itemPrice, 0)
        : summary.minimumPrice
}

function setTraits(user?: ILoginUser): Traits {
    return user
        ? {
              address: {
                  city: user.city,
                  country: 'United States',
                  postalCode: user.zip,
                  state: user.state,
                  street: user.shippingAddress
              },
              avatar: user.imgProfileUrl,
              birthday: user.dob ? parseISO(user.dob) : null,
              createdAt: parseISO(user.dateCreated),
              email: user.email,
              firstName: user.firstName,
              gender: Gender[user.gender],
              id: String(user.userId),
              //   lastName: user.lastName,
              name: `${user.firstName} ${user.lastName}`,
              username: user.userName,
              fb_login_id: user.facebookUserId
          }
        : null
}

//Stripped down GTM calls have been included to support Google Ads Dynamic Remarketing, because
//Segment support have stated they do not support it
function gtmAddToCart(product: Product, user?: ILoginUser) {
    try {
        TagManager.dataLayer({
            dataLayer: {
                event: GTMEvents.AddToCart,
                value: product.price,
                userId: user?.userId,
                items: [mapProductToRemarketing(product)]
            }
        })
    } catch (e) {
        return console.error('GTM Error', e)
    }
}

function gtmRemoveFromCart(product: Product, user?: ILoginUser) {
    try {
        TagManager.dataLayer({
            dataLayer: {
                event: GTMEvents.RemoveFromCart,
                value: product.price,
                userId: user?.userId,
                items: [mapProductToRemarketing(product)]
            }
        })
    } catch (e) {
        return console.error('GTM Error', e)
    }
}

function gtmTagsSale(products: Product[], value: number, user?: ILoginUser) {
    try {
        TagManager.dataLayer({
            dataLayer: {
                event: GTMEvents.Purchase,
                value: value,
                userId: user?.userId,
                items: mapProductsToRemarketing(products)
            }
        })
    } catch (e) {
        return console.error('GTM Error', e)
    }
}

function gtmViewItem(data: JoinEventRes, price: number, user?: ILoginUser) {
    try {
        const { summary } = data
        TagManager.dataLayer({
            dataLayer: {
                event: GTMEvents.ViewItem,
                value: price,
                userId: user?.userId,
                items: [
                    {
                        id: summary.challengeId,
                        google_business_vertical: GoogleBusinessVertical.Retail
                    }
                ]
            }
        })
    } catch (e) {
        return console.error('GTM Error', e)
    }
}

// Decided that at the current moment we aren't using dynamic remarketing data on the page_view events.
// Should we ever choose to do so, this enum and function can be used to map the url path to a specific category for page_view event.

// enum UrlPathMap {
//     'races' = PageType.VirtualRace,
//     'exercises' = PageType.Exercise,
//     'challenges' = PageType.FitnessChallenge,
//     'store' = PageType.Store,
//     'trainers' = PageType.Trainers,
//     'blog' = PageType.Blog,
//     'cart' = PageType.Cart
// }
// const getPageType = (url: string) => {
//     const rootPath = url.split('/')[1]
//     return (UrlPathMap[rootPath] as string) ?? PageType.Misc
// }
